import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

// App-wide default page size for listing pages.
export const DEFAULT_PAGE_SIZE = 12;

/**
 * Selection that survives page changes. `loadAllIds` returns every id
 * matching the current filters/search (scoped by the server), so Select All
 * covers all pages. Selection resets whenever `resetKey` changes.
 */
export function useCrossPageSelection({ total = 0, loadAllIds, resetKey = "" }) {
    const [selected, setSelected] = useState(() => new Set());
    const [loadingAll, setLoadingAll] = useState(false);
    const loaderRef = useRef(loadAllIds);
    loaderRef.current = loadAllIds;

    useEffect(() => { setSelected(new Set()); }, [resetKey]);

    const toggle = useCallback((id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    }), []);
    const clear = useCallback(() => setSelected(new Set()), []);
    const selectAll = useCallback(async () => {
        setLoadingAll(true);
        try {
            const ids = await loaderRef.current();
            setSelected(new Set(ids || []));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not select all records");
        } finally {
            setLoadingAll(false);
        }
    }, []);

    const count = selected.size;
    const allSelected = total > 0 && count >= total;
    const toggleAll = useCallback(() => (allSelected ? clear() : selectAll()), [allSelected, clear, selectAll]);

    return {
        selected, setSelected, count, ids: Array.from(selected),
        isSelected: (id) => selected.has(id),
        toggle, clear, selectAll, toggleAll,
        allSelected, someSelected: count > 0 && !allSelected, loadingAll,
    };
}
